import type { App } from "vue";
import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import CognitoLogin from "@/views/cognito/CognitoLogin.vue";
import CognitoHostedLogin from "@/views/cognito/CognitoHostedLogin.vue";
import CognitoCallback from "@/views/cognito/CognitoCallback.vue";
import CognitoResetPassword from "@/views/cognito/CognitoResetPassword.vue";
import { Pages } from "./pages";
import HomePage from "@/views/home/HomePage.vue";
import FinancialsPage from "@/views/financials/FinancialsPage.vue";
import ScheduleB from "@/views/scheduleB/ScheduleB.vue";
import ScheduleA from "@/views/scheduleA/ScheduleA.vue";
import KosherCertificates from "@/views/kosherCertificates/KosherCertificates.vue";
import TrademarksPage from "@/views/trademarks/TrademarksPage.vue";
import SubmissionsPage from "@/views/submissionsOverview/SubmissionsPage.vue";
import RfrAssignments from "@/views/rfrAssignments/RfrAssignments.vue";
import ContactsPage from "@/views/contacts/ContactsPage.vue";
import CopackersPage from "@/views/copackers/CopackersPage.vue";
import UkdCompanies from "@/views/ukdCompanies/UkdCompanies.vue";
import UkdProducts from "@/views/ukdProducts/UkdProducts.vue";
import UserLogin from "@/views/userLogin/UserLogin.vue";
import WasherPage from "@/views/washer/WasherPage.vue";
import WorkflowPage from "@/views/workflowOverview/WorkflowPage.vue";
import WorkflowDetailIngredientsPage from "@/views/workflowDetailIngredients/WorkflowDetailIngredientsPage.vue";
import WorkflowDetailProductsPage from "@/views/workflowDetailProducts/WorkflowDetailProductsPage.vue";
import FormsAndApplications from "@/views/formsAndApplications/FormsAndApplications.vue";
import IarSubmissionsPage from "@/views/iarSubmissions/SubmissionsPage.vue";
import RcRfrContactsPage from "@/views/rcRfrContacts/RcRfrContactsPage.vue";
import CompanyRcList from "@/views/companyRcList/CompanyRcList.vue";
import MiniCrmPage from "@/views/miniCrm/MiniCrmPage.vue";
import ChometzRCPage from "@/views/chometzRcCompanies/ChometzRCPage.vue";
import ChometzSales from "@/views/chometzSales/ChometzSalesPage.vue";
import InspectionsSubmenu from "@/views/inspections/InspectionsSubmenuPage.vue";
import RfrVisits from "@/views/inspections/RfrVisitsSearch/RfrVisitsPage.vue";
import VisitInspectionForm from "@/views/inspections/VisitInspectionForm/VisitInspectionFormPage.vue";
import TripLogDetails from "@/views/inspections/tripLogDetails/TripLogDetailsPage.vue";
import VisitInspectionExpensesPage from "@/views/inspections/VisitInspectionExpenses/VisitInspectionExpensesPage.vue";
import RfrTripLogPage from "@/views/inspections/RfrTripLog/RfrTripLogPage.vue";
import TravelMapPage from "@/views/travelMap/TravelMapPage.vue";
import VisitsOverviewPage from "@/views/inspections/VisitsOverview/VisitsOverviewPage.vue";
import PlantHistoryPage from "@/views/plantHistory/PlantHistoryPage.vue";
import PlantVisitDetailsPage from "@/views/plantVisitDetails/PlantVisitDetailsPage.vue";
import { setupAuthGuard } from "@/shared/access/authGuard";

export function setupRouter(app: App<Element>) {
  const routes = [
    {
      path: Pages.Home,
      component: HomePage,
    },
    { path: Pages.HomeDefault, component: HomePage },
    { path: Pages.KosherCertificates, component: KosherCertificates },
    { path: Pages.ScheduleB, component: ScheduleB },
    { path: Pages.ScheduleA, component: ScheduleA },
    { path: Pages.Financials, component: FinancialsPage },
    { path: Pages.Contacts, component: ContactsPage },
    { path: Pages.Trademarks, component: TrademarksPage },
    { path: Pages.SubmissionsOverview, component: SubmissionsPage },
    { path: Pages.RfrAssignments, component: RfrAssignments },
    { path: Pages.CopackerFacilities, component: CopackersPage },
    { path: Pages.UkdCompanies, component: UkdCompanies },
    { path: Pages.UkdProducts, component: UkdProducts },
    { path: Pages.UserLogin, component: UserLogin },
    { path: Pages.Washer, component: WasherPage },
    { path: Pages.WorkflowOverview, component: WorkflowPage },
    {
      path: Pages.WorkflowDetailIngredients,
      component: WorkflowDetailIngredientsPage,
    },
    {
      path: Pages.WorkflowDetailProducts,
      component: WorkflowDetailProductsPage,
    },
    { path: Pages.FormsAndApplications, component: FormsAndApplications },
    { path: Pages.IarSubmissions, component: IarSubmissionsPage },
    { path: Pages.RcRfrContacts, component: RcRfrContactsPage },
    { path: Pages.CompanyRcList, component: CompanyRcList },
    { path: Pages.MiniCrm, component: MiniCrmPage },
    { path: Pages.ChometzRcCompanies, component: ChometzRCPage },
    { path: Pages.ChometzSales, component: ChometzSales },
    { path: Pages.InspectionsSubmenu, component: InspectionsSubmenu },
    { path: Pages.RfrVisitsSearch, component: RfrVisits },
    { path: Pages.VisitInspectionForm, component: VisitInspectionForm },
    { path: Pages.TripLogDetails, component: TripLogDetails },
    { path: Pages.EfrTripExpenses, component: VisitInspectionExpensesPage },
    { path: Pages.RfrTripLog, component: RfrTripLogPage },
    { path: Pages.VisitsOverview, component: VisitsOverviewPage },
    { path: Pages.PlantHistory, component: PlantHistoryPage },
    { path: Pages.PlantVisitDetails, component: PlantVisitDetailsPage },
    { path: Pages.TravelMap, component: TravelMapPage },
    { path: Pages.Login, component: CognitoLogin },
    { path: Pages.HostedLogin, component: CognitoHostedLogin },
    { path: Pages.LoginCallback, component: CognitoCallback },
    { path: Pages.ResetPassword, component: CognitoResetPassword },
    {
      path: "/:pathMatch(.*)*",
      redirect: "oudirect/",
    },
  ] as RouteRecordRaw[];

  // Set up auth guard for all routes except login
  const aspxRoutes = [] as RouteRecordRaw[];
  routes
    .filter(
      (r) =>
        r.path != Pages.Login &&
        r.path != Pages.HostedLogin &&
        r.path != Pages.LoginCallback &&
        r.path != Pages.ResetPassword
    )
    .forEach((route: RouteRecordRaw) => {
      route.meta = {
        requiresAuth: true,
      };

      if (!route.redirect && route.path != "/oudirect/")
        aspxRoutes.push({
          ...route,
          path: route.path + ".aspx",
          redirect: route.path,
          children: route.children || [],
        });
    });

  routes.push(...aspxRoutes);

  const router = createRouter({
    history: createWebHistory(),
    routes,
  });
  
  setupAuthGuard(router);

  app.use(router);
}
